/**
 * The API contract, as this app relies on it.
 *
 * Note what is NOT here: the workflow rules. No task name, no resolution label,
 * no branch. Those arrive at runtime from GET /v1/workflows, which is what lets
 * a rule change on the server reach this console with no release. `npm run
 * check:rules` fails the build if any of them appear in this repository.
 *
 * Hand-written against `npm run openapi` in burjmanage-platform. When a screen
 * shows nothing, compare this file with that spec first.
 */

export type TaskType = 'Action' | 'Cost' | 'Income';
export type TaskStatus = 'Pending' | 'InProgress' | 'Done' | 'Closed';
export type Role = 'specialist' | 'manager' | 'admin';

export interface Task {
  id: string; name: string; workflow: string; type: TaskType; status: TaskStatus;
  propertyId: string; propertyName: string | null; roomId: string | null; leaseId: string | null;
  assigneeId: string | null; dueDate: string; description: string;
  amount: number; currency: string; version: number;
  severity: string | null; source: string | null; category: string | null;
  receiptId: string | null; parentId: string | null; resolution: string | null;
}

/** A rule as the engine serves it. The shape is ours; the content is never. */
export interface Rule {
  name: string; workflow: string; type: TaskType; manualEntry: boolean;
  next: string[]; special: string | null;
  question: {
    prompt: string;
    options: Array<{
      label: string; detail?: string; next: string[];
      pauseRent?: boolean; resumeRent?: boolean; createsLease?: boolean;
    }>;
  } | null;
}

export interface Catalogue {
  rules: Rule[];
  adhoc: Record<string, Array<{ name: string; type: TaskType }>>;
}

export type PropertyStatus = 'UnderTender' | 'Maintenance' | 'Operating' | 'Released';

/** A villa stands alone; a whole building, or one floor of one, belongs to a building. */
export type PropertyType = 'Villa' | 'Building' | 'Floor';

export interface Property {
  id: string; name: string; address: string; city: string; district: string;
  status: PropertyStatus; roomCount: number; occupiedCount: number; rentRoll: number;
  /** A building or floor has its building's position: moving the building moves it. */
  latitude: number | null; longitude: number | null;
  kind: PropertyType; buildingId: string | null; floor: string | null;
  cityId: string | null; districtId: string | null;
}

/** A tower or standalone building: the name and the pin its properties share. */
export interface Building {
  id: string; name: string; address: string;
  cityId: string | null; districtId: string | null; city: string; district: string;
  latitude: number | null; longitude: number | null; propertyCount?: number;
}

/** Places of operation, kept in the admin console: what the dropdowns offer. */
export interface Locations { countries: LocCountry[] }
export interface LocCountry { code: string; name: string; cities: LocCity[] }
export interface LocCity {
  id: string; name: string; latitude: number | null; longitude: number | null;
  /** south, west, north, east */
  bbox: [number, number, number, number] | null;
  districts: LocDistrict[];
}
export interface LocDistrict { id: string; name: string; latitude: number | null; longitude: number | null }

export interface Room {
  id: string; propertyId: string; number: string; status: 'Vacant' | 'Occupied';
  tenantId: string | null; askingLow: number; askingHigh: number; currentRent: number;
}

export interface Lease {
  id: string; tenantId: string; propertyId: string; roomId: string;
  tenantName: string | null; roomNumber: string | null;
  startDate: string; endDate: string; monthlyRent: number; depositAmount: number;
  status: 'Active' | 'MovingOut' | 'Ended';
  depositStatus: 'Pending' | 'Paid' | 'Refunded';
  rentPausedOn: string | null;
}

export type SkipReason =
  | 'lease_ended' | 'moving_out' | 'rent_paused' | 'lease_expires_first' | 'already_scheduled'
  | 'not_started';

export interface RentCycle {
  windowOpensOn: string; windowOpen: boolean; willCreate: number; totalValue: number;
  plan: Array<{ leaseId: string; roomId: string; amount: number; dueDate: string; skip: SkipReason | null }>;
}

export interface Employee {
  id: string; name: string; email: string; phone: string | null;
  role: Role; is_active: boolean;
  open_tasks: number; overdue_tasks: number;
}

/** The languages residents' texts and emails are written in; the platform keeps one per resident. */
export const TEXT_LANGUAGES = {
  en: 'English', ar: 'العربية', ur: 'اردو', hi: 'हिन्दी', ru: 'Русский',
  fa: 'فارسی', zh: '中文', es: 'Español', fr: 'Français', fil: 'Filipino',
} as const;
export type TextLanguage = keyof typeof TEXT_LANGUAGES;

export interface Tenant {
  id: string; name: string; phone: string; email: string | null; nationality: string | null;
  /** Set when they sign in, in the language they chose; the office can set it first. */
  language?: TextLanguage;
  /** active: they have proved their email and phone and can sign in; invited: not yet */
  account?: 'active' | 'invited';
  lease_id: string | null; room_number: string | null; property_name: string | null;
  monthly_rent: number | null; end_date: string | null;
}

export interface FinanceSummary {
  collected: number; spent: number; expected_in: number; committed_out: number;
}

export type PaymentMethod = 'BankTransfer' | 'Cash' | 'CreditCard' | 'Cheque';

export interface Payment {
  id: string; amount: number; currency: string; received_on: string;
  method: PaymentMethod; kind: 'Rent' | 'Deposit'; task_id: string | null;
  property_id: string; tenant_id: string | null; lease_id: string | null; receipt_id: string | null;
  /** Brought in with the company's existing data: before BurjManage, so no receipt. */
  import_id?: string | null;
  /** For imported history: the resident's name, when they have since left. */
  note?: string | null;
}

export interface Cost {
  id: string; amount: number; currency: string; paid_on: string;
  category: 'Initial' | 'Fixed' | 'Variable'; description: string; task_id: string | null;
  property_id: string; room_id: string | null; receipt_id: string | null;
  import_id?: string | null;
}

export interface Camera {
  id: string; property_id: string; location: string; last_seen: string | null;
  /** credentials removed by the API */
  stream_url: string; has_credentials: boolean;
}

export interface TaskEvent {
  id: number; event: string; from_status: string | null; to_status: string | null;
  resolution: string | null; actor_type: string; surface: string; created_at: string;
  payload: Record<string, unknown> | null;
}

export interface TaskDetail {
  task: Task;
  events: TaskEvent[];
  children: Array<{ id: string; name: string; status: string }>;
  rule: Rule | null;
}

export interface Session {
  accessToken: string; expiresIn: number;
  /** Buys the next access token; absent on a support session, which simply ends. */
  refreshToken?: string;
  staff: { id: string; name: string; role: Role; orgId: string };
  /** Present when a platform administrator opened this from the admin console. */
  support?: { readOnly: boolean; expiresAt: number };
}

export interface CompleteResult {
  task: { id: string; status: 'Done'; version: number };
  createdTasks: Array<{ id: string; name: string; workflow: string; type: TaskType; dueDate: string; amount: number }>;
  chainClosed: boolean;
}

export interface Presigned {
  receiptId: string; uploadUrl: string; storageKey: string;
  expiresInSeconds: number; method: 'PUT'; headers: Record<string, string>;
}

export interface LeaseDraft {
  tenantId: string; roomId: string; startDate: string; endDate: string;
  monthlyRent: number; depositAmount: number;
}

/** What the company owes BurjManage, and what happens if it does not pay. */
export interface BillingInvoice {
  id: string; number: string; period: string; peakRooms: number;
  subtotal: number; vat: number; total: number;
  status: 'Open' | 'Paid' | 'Overdue' | 'Void';
  issuedOn: string; dueOn: string; paidOn: string | null;
}
export interface PayTo {
  legalName: string; address: string; trn: string; email: string;
  bankName: string; accountName: string; iban: string; swift: string;
}
export interface Billing {
  org: {
    name: string; status: 'Active' | 'Trialing' | 'PastDue' | 'Suspended' | 'Cancelled';
    /** While trialing: the day it becomes the paid plan. */
    trialEndsOn: string | null;
  };
  plan: {
    code: string; version: number; baseFee: number; perRoomFee: number;
    roomsAllowance: number; seatsLimit: number; propertiesLimit: number; renewsOn: string;
  } | null;
  usage: { rooms: number; properties: number; seats: number };
  /** Deadlines run from the oldest unpaid invoice's due date. */
  standing: {
    status: Billing['org']['status']; owed: number; overdue: number;
    oldestDueOn: string | null; readOnlyOn: string | null; suspendOn: string | null;
  };
  invoices: BillingInvoice[];
  payTo: PayTo;
}

/** Someone invited who has not yet set their password. */
export interface Invitation {
  id: string; email: string; name: string; role: Role;
  expires_at: string; created_at: string; expired: boolean; invited_by: string | null;
}
